import { useLayoutEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useRealtimeInvalidate } from "@/hooks/realtime/useRealtimeInvalidate";
import { realtimeScopeId, realtimeTopic, type PgChangePayload } from "@/lib/realtime/channel";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { briefKeys } from "../queryKeys";

/**
 * Page-level: the brief's versions and appendices (with their replies and
 * attachments), filtered by brief id (those tables carry no building_id).
 * Events are signals — default replica identity, so `payload.old` is `{id}` —
 * and every brief cache is flushed by prefix; only the mounted project's
 * entries are active. Franklin's suggestions arrive on the building channel
 * (`useBriefBuildingRealtime`: project_brief_findings carries building_id).
 *
 * Document-row changes (a new version of a member, a verified flip, a
 * delete) arrive through the building's existing `documents` bridge; the
 * page subscribes to that with `useDocumentsRealtimeFlush`, never a second
 * channel on `documents`.
 *
 * Version deletions are bound only once their table is known to exist
 * (`deletionsAvailable`): a binding on a table the database lacks fails the
 * whole channel, versions and additions with it.
 */
export function useBriefRealtime(
  briefId: number | null,
  deletionsAvailable: boolean | undefined
): void {
  const id = realtimeScopeId(briefId);
  useRealtimeInvalidate({
    enabled: id !== null && deletionsAvailable !== undefined,
    topic: realtimeTopic("brief", id),
    flushOnJoin: true,
    bindings: [
      { event: "*", table: "project_brief_versions", filter: `brief_id=eq.${id}` },
      ...(deletionsAvailable
        ? [
            {
              event: "*" as const,
              table: "project_brief_version_deletions",
              filter: `brief_id=eq.${id}`,
            },
          ]
        : []),
      { event: "*", table: "project_brief_appendices", filter: `brief_id=eq.${id}` },
      { event: "*", table: "project_brief_appendix_replies", filter: `brief_id=eq.${id}` },
      { event: "*", table: "project_brief_appendix_documents", filter: `brief_id=eq.${id}` },
    ],
    invalidate: [{ queryKey: briefKeys.all }],
  });
}

interface CurrentLocation {
  pathname: string;
  search: string;
}

/**
 * Is the user looking at this project's brief right now? Path and the scope
 * param are compared separately — an extra, reordered or legacy (`projectId`)
 * param must not defeat the check.
 */
export function isOnBriefPage(
  location: CurrentLocation,
  buildingId: number,
  projectId: number | null
): boolean {
  if (location.pathname !== `/buildings/${buildingId}/brief`) return false;
  if (projectId === null) return true;
  const params = new URLSearchParams(location.search);
  const scoped = params.get("project") ?? params.get("projectId");
  return scoped !== null && Number(scoped) === projectId;
}

interface CheckRowLike {
  id?: unknown;
  status?: unknown;
  project_id?: unknown;
  building_id?: unknown;
}

/**
 * Layout-level: check runs and findings for the whole building, filtered by
 * building_id (the one server-side filter realtime allows; RLS remains the
 * gate). Feeds the nav dot and the completion toast from any page under the
 * building — a run that finishes while the user is on the Issues page is
 * announced there, with a Review action that lands on the brief.
 *
 * Toasted ids are tracked because `payload.old` only carries the key: a
 * later write to a completed row (none today, but the organiser grew one)
 * must not re-toast.
 */
export function useBriefBuildingRealtime(buildingId: string | number | null | undefined): void {
  const id = realtimeScopeId(buildingId);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const toasted = useRef(new Set<number>());

  // Read through refs inside the long-lived binding: the strings must follow a
  // language switch and navigate must be the current one, without churning
  // the channel (the hook already reads bindings through a ref, so these are
  // about freshness, not subscription identity).
  const copyRef = useRef(t.brief.run);
  const navigateRef = useRef(navigate);
  const pathRef = useRef<CurrentLocation>({ pathname: location.pathname, search: location.search });
  useLayoutEffect(() => {
    copyRef.current = t.brief.run;
    navigateRef.current = navigate;
    pathRef.current = { pathname: location.pathname, search: location.search };
  }, [t.brief.run, navigate, location.pathname, location.search]);

  useRealtimeInvalidate({
    enabled: id !== null,
    topic: realtimeTopic("brief-building", id),
    flushOnJoin: true,
    bindings: [
      {
        event: "*",
        table: "project_brief_check_requests",
        filter: `building_id=eq.${id}`,
        handler: (payload: PgChangePayload) => {
          const row = payload.new as CheckRowLike;
          if (row?.status !== "completed" || typeof row.id !== "number") return;
          if (toasted.current.has(row.id)) return;
          toasted.current.add(row.id);
          const projectId = typeof row.project_id === "number" ? row.project_id : null;
          const target = `/buildings/${id}/brief${projectId === null ? "" : `?project=${projectId}`}`;
          // The panel itself is live on the brief page; a toast there is noise.
          if (isOnBriefPage(pathRef.current, id, projectId)) return;
          toast.success(copyRef.current.completedToast, {
            action: { label: copyRef.current.review, onClick: () => navigateRef.current(target) },
          });
        },
      },
      { event: "*", table: "project_brief_findings", filter: `building_id=eq.${id}` },
    ],
    invalidate: [
      { queryKey: briefKeys.checksAll },
      { queryKey: briefKeys.findingsAll },
      { queryKey: briefKeys.unconfirmedAll },
      { queryKey: briefKeys.openCountAll },
    ],
  });
}
